/**
 * export async function fetchIsoData(selectedList) { ... }
 * Fetches ISO data for the given list of selected entities.
 *
 * Calls the API with the stored procedure 'getISODataForEntities' and processes the response
 * to build a maintenance map keyed by Entity_DBID. Each entity's metadata is mapped to a
 * specific key (e.g., 'ServiceInformation', 'Periodicity', etc.) based on its EntityMetaType.
 *
 * @async
 * @param {Array} selectedList - The list of selected entity IDs to fetch ISO data for.
 * @returns {Promise<Object|null>} A map of entity IDs to their ISO maintenance data, or null if no data is found or on error.
 */

/**
 * export async function fetchIsoFields() { ... }
 * Fetches the available ISO maintenance fields.
 *
 * Calls the API with the stored procedure 'getISOMaintenanceFields' and processes the response
 * to build an object mapping each EntityMetaType to its field options, including label, data type,
 * database ID, and available options.
 *
 * @async
 * @returns {Promise<Object|null>} An object mapping field types to their metadata and options, or null if no data is found or on error.
 */

import { Api } from './interface';

export async function fetchIsoData(selectedList) {
  if (
    !selectedList ||
    !Array.isArray(selectedList) ||
    selectedList.length === 0
  ) {
    return null;
  }
  try {
    const response = await Api({
      sp: 'getISODataForEntities',
      json: {
        selectedList: selectedList,
      },
    });
    if (Array.isArray(response) && response.length > 0) {
      let maintenanceMap = {};
      response.forEach((entity) => {
        if (!maintenanceMap[entity.Entity_DBID]) {
          maintenanceMap[entity.Entity_DBID] = {};
        }
      });
      response.forEach((entity) => {
        if (maintenanceMap[entity.Entity_DBID]) {
          let key = '';
          switch (entity.EntityMetaType) {
            case 'Service Information':
              key = 'ServiceInformation';
              break;
            case 'Periodicity':
              key = 'Periodicity';
              break;
            case 'Last Service Date':
              key = 'LastServiceDate';
              break;
            case 'Next Service Date':
              key = 'NextServiceDate';
              break;
            default:
              break;
          }
          maintenanceMap[entity.Entity_DBID][key] = {
            EntityMetaType_DBID: entity.EntityMetaType_DBID,
            EntityMetaValue: entity.EntityMetaValue,
          };
        }
      });
      if (Object.keys(maintenanceMap).length === 0) {
        maintenanceMap = null;
      }
      return maintenanceMap;
    }
    return null;
  } catch (error) {
    console.error('Failed to fetch ISO data:', error);
    return null;
  }
}

export async function fetchIsoFields() {
  try {
    const response = await Api({
      sp: 'getISOMaintenanceFields',
      json: {},
    });
    if (Array.isArray(response) && response.length > 0) {
      const isoFieldOptions = {};
      response.forEach((item) => {
        const key = item.EntityMetaType;
        if (!isoFieldOptions[key]) {
          isoFieldOptions[key] = {
            Label: item.EntityMetaType,
            DataType: item.DataType,
            EntityMetaType_DBID: item.EntityMetaType_DBID,
            EntityMetaOption: [],
          };
        }
        if (item.EntityMetaOption) {
          isoFieldOptions[key].EntityMetaOption.push(item.EntityMetaOption);
        }
      });
      return isoFieldOptions;
    }
    return null;
  } catch (error) {
    console.error('Failed to fetch ISO fields:', error);
    return null;
  }
}
